import React, { useState } from "react";
import axios from "axios";
import { useMutation } from "react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "components/button";
import TableComponent from "components/table";
import { useTable } from "components/table";
import SelectV2 from "components/react-selectv2";
import "./styles.css";

type StripePaymentMetadata = {
  type?: string;
  id?: string;
  is_deposit?: string | boolean;
  [key: string]: string | boolean | undefined;
};

type StripePayment = {
  id: string;
  amount: number;
  amount_received?: number;
  currency: string;
  status: string;
  created: number;
  customer: string | null;
  description: string | null;
  receipt_url: string | null;
  payment_method: string | null;
  metadata?: StripePaymentMetadata;
  payment_method_details: {
    type: string;
    card?: {
      brand: string;
      last4: string;
      exp_month: number;
      exp_year: number;
    };
  } | null;
};

type ImportedPaymentResult = {
  payment_intent_id: string;
  payment_id: number;
  invoice_id: number | null;
  applied: number | null;
  status: number | null;
};

type SkippedPaymentResult = {
  payment_intent_id: string;
  reason: string;
};

type ErroredPaymentResult = {
  payment_intent_id: string;
  message: string;
};

type ImportPaymentsResponse = {
  imported: ImportedPaymentResult[];
  skipped: SkippedPaymentResult[];
  errors: ErroredPaymentResult[];
};

const INVOICE_STATUS_LABELS: Record<number, string> = {
  18: "Paid",
  38: "Partially Paid",
  37: "Processing",
};

const StripeManagement: React.FC = () => {
  const { t } = useTranslation();
  const tBase = "views.admin.stripeManagement";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const [stripeAccountId, setStripeAccountId] = useState<string>("");
  const [selectedCompany, setSelectedCompany] = useState<{
    name: string;
    stripe_account_id: string;
  } | null>(null);
  const [payments, setPayments] = useState<StripePayment[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPayments, setSelectedPayments] = useState<Set<string>>(new Set());
  const [includeProcessing, setIncludeProcessing] = useState<boolean>(true);
  const [sendReceiptEmails, setSendReceiptEmails] = useState<boolean>(false);
  const [importResults, setImportResults] = useState<ImportPaymentsResponse | null>(null);

  const table = useTable();

  const [companyAccounts, setCompanyAccounts] = useState<
    { name: string; stripe_account_id: string }[]
  >([]);
  const [companyLoading, setCompanyLoading] = useState<boolean>(false);
  const [companyError, setCompanyError] = useState<string | null>(null);

  const getAccountId = () =>
    selectedCompany?.stripe_account_id || stripeAccountId;

  React.useEffect(() => {
    const fetchCompanyAccounts = async () => {
      setCompanyLoading(true);
      setCompanyError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/super-admin/accounts`
        );
        setCompanyAccounts(response.data.accounts || []);
      } catch (err: any) {
        setCompanyError(
          err.response?.data?.message ||
            err.message ||
            tr("Failed to fetch company Stripe accounts")
        );
      } finally {
        setCompanyLoading(false);
      }
    };
    fetchCompanyAccounts();
  }, []);

  const fetchStripePayments = useMutation(
    async ({
      accountId,
      includeProcessing: includeProcessingParam,
      silent = false,
      preserveImportResults = false,
    }: {
      accountId: string;
      includeProcessing: boolean;
      silent?: boolean;
      preserveImportResults?: boolean;
    }) => {
      if (!silent) {
        setIsLoading(true);
        setError(null);
        setSelectedPayments(new Set());
        if (!preserveImportResults) {
          setImportResults(null);
        }
      }

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/super-admin/fetch-payments`,
          {
            stripe_account_id: accountId,
            include_processing: includeProcessingParam,
          }
        );

        setPayments(response.data.payments || []);
        return response.data;
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          tr("Failed to fetch Stripe payments");
        setError(errorMessage);
        throw err;
      } finally {
        if (!silent) {
          setIsLoading(false);
        }
      }
    },
    {
      onError: (err: any) => {
        console.error("Error fetching Stripe payments:", err);
      },
    }
  );

  const importPayments = useMutation(
    async ({
      paymentIds,
      sendReceiptEmails: sendReceiptEmailsParam,
    }: {
      paymentIds: string[];
      sendReceiptEmails: boolean;
    }) => {
      const accountId = getAccountId();
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/super-admin/import-payments`,
        {
          stripe_account_id: accountId,
          payment_ids: paymentIds,
          send_receipt_emails: sendReceiptEmailsParam,
        }
      );

      const results: ImportPaymentsResponse = {
        imported: response.data.imported || [],
        skipped: response.data.skipped || [],
        errors: response.data.errors || [],
      };
      setImportResults(results);
      setSelectedPayments(new Set());

      const removedFromTable = new Set([
        ...results.imported.map((row) => row.payment_intent_id),
        ...results.skipped.map((row) => row.payment_intent_id),
      ]);
      if (removedFromTable.size > 0) {
        setPayments((prev) => prev.filter((p) => !removedFromTable.has(p.id)));
      }

      if (accountId) {
        fetchStripePayments.mutate({
          accountId,
          includeProcessing,
          silent: true,
          preserveImportResults: true,
        });
      }

      return results;
    },
    {
      onError: (err: any) => {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          tr("Failed to import payments");
        setError(errorMessage);
        console.error("Error importing payments:", err);
      },
    }
  );

  const handleFetchPayments = () => {
    const accountId = getAccountId();

    if (!accountId.trim()) {
      setError(tr("Please select a company or enter a Stripe account ID"));
      return;
    }

    if (!accountId.startsWith("acct_")) {
      setError(tr("Stripe account ID must start with 'acct_'"));
      return;
    }

    fetchStripePayments.mutate({ accountId, includeProcessing });
  };

  const handleImportSelected = (selectedPaymentIds: string[]) => {
    if (selectedPaymentIds.length === 0) {
      setError(tr("Please select at least one payment to import"));
      return;
    }

    setError(null);
    importPayments.mutate({
      paymentIds: selectedPaymentIds,
      sendReceiptEmails,
    });
  };

  const handleRowClick = (payment: StripePayment) => {
    setSelectedPayments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(payment.id)) {
        newSet.delete(payment.id);
      } else {
        newSet.add(payment.id);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedPayments.size === payments.length) {
      setSelectedPayments(new Set());
    } else {
      setSelectedPayments(new Set(payments.map((p) => p.id)));
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatMetadataType = (type?: string) => {
    if (!type) return tr("No metadata");
    const labels: Record<string, string> = {
      invoice: tr("Invoice"),
      fk_invoice_id: tr("Invoice"),
      request: tr("Request"),
      fk_request_id: tr("Request"),
      project: tr("Project"),
      fk_project_id: tr("Project"),
      customer: tr("Customer"),
      fk_bcustomer_id: tr("Customer"),
      "deposit-request": tr("Deposit Request"),
    };
    return labels[type] || type;
  };

  const formatIsDeposit = (value?: string | boolean) => {
    if (value === true || value === "true") return tr("Yes");
    if (value === false || value === "false") return tr("No");
    return "—";
  };

  const formatSkipReason = (reason: string) => {
    if (reason === "already_exists") return tr("Already imported");
    return reason;
  };

  const formatInvoiceStatus = (status: number | null) => {
    if (status == null) return "—";
    const label = INVOICE_STATUS_LABELS[status];
    return label ? tr(label) : String(status);
  };

  const hasFetched = payments.length > 0 || (fetchStripePayments.isSuccess && !isLoading);

  const columns = {
    selection: {
      label: "",
      width: "50px",
      disableSort: true,
      cell: (row: StripePayment) => (
        <div className="flex items-center justify-center">
          <div
            className={`w-4 h-4 rounded border-2 flex items-center justify-center stripe-selection-checkbox ${
              selectedPayments.has(row.id)
                ? "bg-blue-500 border-blue-500"
                : "border-gray-300"
            }`}
          >
            {selectedPayments.has(row.id) && (
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
        </div>
      ),
    },
    id: {
      label: tr("Payment ID"),
      cell: (row: StripePayment) => (
        <div className="font-mono text-sm break-all">{row.id}</div>
      ),
    },
    amount: {
      label: tr("Amount"),
      cell: (row: StripePayment) => (
        <div className="font-semibold">{formatAmount(row.amount, row.currency)}</div>
      ),
    },
    status: {
      label: tr("Status"),
      cell: (row: StripePayment) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.status === "succeeded"
              ? "bg-green-100 text-green-800"
              : row.status === "processing"
              ? "bg-yellow-100 text-yellow-800"
              : row.status === "failed"
              ? "bg-red-100 text-red-800"
              : "bg-gray-100 text-gray-800"
          }`}
          title={
            row.status === "processing"
              ? tr("Processing import warning")
              : undefined
          }
        >
          {row.status}
        </span>
      ),
    },
    metadata_type: {
      label: tr("Metadata Type"),
      cell: (row: StripePayment) => (
        <div className="text-sm">{formatMetadataType(row.metadata?.type)}</div>
      ),
    },
    metadata_id: {
      label: tr("Linked Entity ID"),
      cell: (row: StripePayment) => (
        <div className="text-sm font-mono">
          {row.metadata?.id || "—"}
        </div>
      ),
    },
    is_deposit: {
      label: tr("Deposit"),
      cell: (row: StripePayment) => (
        <div className="text-sm">{formatIsDeposit(row.metadata?.is_deposit)}</div>
      ),
    },
    created: {
      label: tr("Created"),
      cell: (row: StripePayment) => (
        <div className="text-sm text-gray-600">{formatDate(row.created)}</div>
      ),
    },
    customer: {
      label: tr("Customer"),
      cell: (row: StripePayment) => (
        <div className="text-sm">{row.customer || tr("No customer")}</div>
      ),
    },
    description: {
      label: tr("Description"),
      cell: (row: StripePayment) => (
        <div className="text-sm text-gray-600 max-w-xs truncate">
          {row.description || tr("No description")}
        </div>
      ),
    },
    payment_method: {
      label: tr("Payment Method"),
      cell: (row: StripePayment) => {
        if (row.payment_method_details?.type === "card" && row.payment_method_details.card) {
          const { brand, last4, exp_month, exp_year } = row.payment_method_details.card;
          return (
            <div className="text-sm">
              <div className="capitalize">{brand}</div>
              <div className="text-gray-500">**** {last4}</div>
              <div className="text-gray-400 text-xs">
                {exp_month}/{exp_year}
              </div>
            </div>
          );
        }
        return (
          <div className="text-sm text-gray-500">
            {row.payment_method_details?.type || tr("Unknown")}
          </div>
        );
      },
    },
    receipt_url: {
      label: tr("Receipt"),
      cell: (row: StripePayment) => (
        <div>
          {row.receipt_url ? (
            <a
              href={row.receipt_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              {tr("View Receipt")}
            </a>
          ) : (
            <span className="text-gray-400 text-sm">{tr("No receipt")}</span>
          )}
        </div>
      ),
    },
  };

  const CustomTableWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    React.useEffect(() => {
      const tableRows = document.querySelectorAll("tbody tr");
      tableRows.forEach((row, index) => {
        const payment = payments[index];
        if (payment && selectedPayments.has(payment.id)) {
          row.classList.add("stripe-table-row-selected");
        } else {
          row.classList.remove("stripe-table-row-selected");
        }
      });
    }, [selectedPayments, payments]);

    return <div className="relative">{children}</div>;
  };

  return (
    <div className="p-6 max-w-[100rem] mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {tr("Stripe Payment Management")}
        </h1>
        <p className="text-gray-600">{tr("Page description")}</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        {companyAccounts.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {tr("Select Company")}
            </label>
            <SelectV2
              options={companyAccounts.map((company) => ({
                value: company.stripe_account_id,
                label: `${company.name} (${company.stripe_account_id})`,
              }))}
              value={
                selectedCompany
                  ? {
                      value: selectedCompany.stripe_account_id,
                      label: `${selectedCompany.name} (${selectedCompany.stripe_account_id})`,
                    }
                  : null
              }
              onChange={(option: any) => {
                if (option) {
                  const company = companyAccounts.find(
                    (c) => c.stripe_account_id === option.value
                  );
                  setSelectedCompany(company || null);
                  setStripeAccountId("");
                  setIncludeProcessing(true);
                } else {
                  setSelectedCompany(null);
                }
              }}
              placeholder={tr("Choose a company...")}
              isClearable
              isLoading={companyLoading}
            />
            {companyError && (
              <p className="text-xs text-red-500 mt-1">{companyError}</p>
            )}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 sm:items-end sm:justify-between">
          <div className="flex flex-col gap-3">
            {/* <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeProcessing}
                onChange={(e) => setIncludeProcessing(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                {tr("Include ACH")}
              </span>
            </label>
            <p className="text-xs text-gray-500 max-w-md">
              {tr("Include ACH helper")}
            </p> */}
          </div>
          <Button
            onClick={handleFetchPayments}
            disabled={isLoading || !getAccountId().trim()}
            isLoading={isLoading}
            primary
          >
            {isLoading ? tr("Fetching...") : tr("Fetch Payments")}
          </Button>
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{tr("Error")}</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {importResults && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {tr("Import Results")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-100">
              <div className="text-sm text-green-700 font-medium">{tr("Imported")}</div>
              <div className="text-2xl font-bold text-green-900">
                {importResults.imported.length}
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
              <div className="text-sm text-blue-700 font-medium">{tr("Skipped")}</div>
              <div className="text-2xl font-bold text-blue-900">
                {importResults.skipped.length}
              </div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg border border-red-100">
              <div className="text-sm text-red-700 font-medium">{tr("Errors")}</div>
              <div className="text-2xl font-bold text-red-900">
                {importResults.errors.length}
              </div>
            </div>
          </div>

          {importResults.imported.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-900 mb-2">{tr("Imported")}</h3>
              <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md">
                {importResults.imported.map((row) => (
                  <li key={row.payment_intent_id} className="px-4 py-3 text-sm">
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      <span className="font-mono text-gray-600">{row.payment_intent_id}</span>
                      <Link
                        to={`/payments/${row.payment_id}`}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        {tr("Payment")} #{row.payment_id}
                      </Link>
                      {row.invoice_id != null && (
                        <>
                          <Link
                            to={`/invoices/${row.invoice_id}`}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            {tr("Invoice")} #{row.invoice_id}
                          </Link>
                          {row.applied != null && (
                            <span className="text-gray-600">
                              {tr("Applied Amount")}: {formatAmount(row.applied, "usd")}
                            </span>
                          )}
                          {row.status != null && (
                            <span className="text-gray-600">
                              {tr("Invoice Status")}: {formatInvoiceStatus(row.status)}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {importResults.skipped.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-900 mb-2">{tr("Skipped")}</h3>
              <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md">
                {importResults.skipped.map((row) => (
                  <li
                    key={row.payment_intent_id}
                    className="px-4 py-3 text-sm flex flex-wrap gap-x-4"
                  >
                    <span className="font-mono text-gray-600">{row.payment_intent_id}</span>
                    <span className="text-blue-700">{formatSkipReason(row.reason)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {importResults.errors.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">{tr("Errors")}</h3>
              <ul className="divide-y divide-gray-100 border border-red-200 rounded-md bg-red-50">
                {importResults.errors.map((row) => (
                  <li key={row.payment_intent_id} className="px-4 py-3 text-sm">
                    <span className="font-mono text-gray-700">{row.payment_intent_id}</span>
                    <span className="text-red-700 ml-4">{row.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {payments.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
              <div className="flex flex-wrap items-center gap-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  {tr("Stripe Payments not in EServ")} ({payments.length})
                </h2>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={
                      selectedPayments.size === payments.length && payments.length > 0
                    }
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">{tr("Select All")}</span>
                </label>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                {/* <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={sendReceiptEmails}
                    onChange={(e) => setSendReceiptEmails(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {tr("Send receipt emails to customers")}
                  </span>
                </label> */}
                <Button
                  onClick={() => handleImportSelected(Array.from(selectedPayments))}
                  disabled={importPayments.isLoading || selectedPayments.size === 0}
                  isLoading={importPayments.isLoading}
                  primary
                >
                  {importPayments.isLoading ? tr("Importing...") : tr("Import Selected")}
                </Button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto stripe-table-container">
            <CustomTableWrapper>
              <TableComponent
                data={payments}
                columns={columns}
                {...table}
                handleClick={handleRowClick}
              />
            </CustomTableWrapper>
          </div>
        </div>
      )}

      {payments.length === 0 && !isLoading && hasFetched && getAccountId() && !error && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {tr("No payments found")}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {tr(
                "All Stripe payments are already imported into EServ or no payments exist for this account."
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default StripeManagement;
